/**
 * «Хозяйства» (реестр) и «Хозяйство» (карточка) — чистая логика без разметки
 * и запросов (agroai-6al.9, план `docs/research/2026-10-03-page-plan-v2.md`
 * §4–§5).
 *
 * Сигналы хозяйства до серверного счётчика 6al.2 собираются на клиенте:
 * * сроки, вода, расхождения — строка очереди ситуаций сервера (`/situations`,
 *   линза «все»: порог существенности считает сервер, клиент его не
 *   изобретает);
 * * карантин — статусы карантина × реестр контуров (контур относится к
 *   хозяйству, названному в реестре землепользователем; совместные
 *   землепользования этим способом не видны — оговорка печатается).
 *
 * Отсутствие ≠ ноль: `null` — «не считали», `0` — «сигнала нет».
 */

import type { ValuesLayer } from '../map/MapWidget';
import type { SignalCounts } from '../astra/problems';
import { defineMessages } from '../../i18n/define';
import { tr } from '../../i18n/runtime';
import { ha as haText } from '../lib/format';

const m = defineMessages({
  areaNone: { id: 'farm.registry.area.none', defaultMessage: 'Площадь хозяйства в реестре не названа — это пробел данных, а не ноль.', description: 'Пояснение к площади хозяйства, когда в реестре её нет' },
  areaDiffers: { id: 'farm.registry.area.differs', defaultMessage: 'Земля — вся площадь землепользований хозяйства по реестру ({total} га). Пашня из них — {arable} га: без дорог, лесополос и построек. Это разные величины, а не расхождение.', description: 'Пояснение к площади хозяйства, когда пашня отличается от всей земли; {total}/{arable} — гектары строкой с двумя знаками' },
  areaSame: { id: 'farm.registry.area.same', defaultMessage: 'Земля — вся площадь землепользований хозяйства по реестру.', description: 'Пояснение к площади хозяйства, когда пашня совпадает со всей землёй' },
  layerTitle: { id: 'farm.registry.layer.title', defaultMessage: 'Сигналы хозяйств', description: 'Название слоя карты в реестре хозяйств: контур красится по худшему сигналу хозяйства' },
  layerQuarantine: { id: 'farm.registry.layer.quarantine', defaultMessage: 'Карантин: красный', description: 'Строка легенды слоя «Сигналы хозяйств»: контур в красном карантине' },
  layerWatch: { id: 'farm.registry.layer.watch', defaultMessage: 'Карантин: наблюдение', description: 'Строка легенды слоя «Сигналы хозяйств»: контур в жёлтом статусе (наблюдение)' },
  layerDeadlines: { id: 'farm.registry.layer.deadlines', defaultMessage: 'Сроки: нет отчёта после срока', description: 'Строка легенды слоя «Сигналы хозяйств»: у хозяйства работы после срока без отчёта' },
  layerConflicts: { id: 'farm.registry.layer.conflicts', defaultMessage: 'Расхождение отчёта и проверки', description: 'Строка легенды слоя «Сигналы хозяйств»: отчёт и проверка не сходятся' },
  layerWater: { id: 'farm.registry.layer.water', defaultMessage: 'Вода: подача не закрыта', description: 'Строка легенды слоя «Сигналы хозяйств»: полив без закрытой подачи' },
  layerNone: { id: 'farm.registry.layer.none', defaultMessage: 'Сигналов нет', description: 'Подпись контуров без сигналов на слое «Сигналы хозяйств»' },
});


/* ------------------------------------------------------------ культуры */

/** Служебные коды размещения, не являющиеся культурой. */
const NOT_A_CROP = new Set(['DETAIL_TOTAL', 'TOTAL', 'SOWN_TOTAL', 'UNKNOWN', 'OTHER']);

/**
 * Коды культур из направлений хозяйства (`/actors` → `directions[].crop_code`):
 * без повторов, без пустых и «unknown» — незнание не печатается как культура.
 */
export function cropCodes(directions: ReadonlyArray<{ crop_code: string | null }>): string[] {
  const out: string[] = [];
  for (const d of directions) {
    const code = d.crop_code?.trim().toUpperCase();
    if (!code || NOT_A_CROP.has(code) || out.includes(code)) continue;
    out.push(code);
  }
  return out;
}

/**
 * Культуры размещения сезона из карточки хозяйства (`placements[].rows[].measures`):
 * код → площадь, га; итоговые строки отброшены, по площади сверху.
 */
export function placementCrops(
  placements: ReadonlyArray<{ season: string; rows: ReadonlyArray<{ measures: ReadonlyArray<{ metric_code: string; area_microha: number | null }> }> }>,
  season: string,
): { code: string; ha: number | null }[] {
  const byCode = new Map<string, number | null>();
  for (const block of placements) {
    if (block.season !== season) continue;
    for (const row of block.rows) {
      for (const m of row.measures) {
        const code = m.metric_code.toUpperCase();
        if (NOT_A_CROP.has(code) || code.includes('TOTAL') || code.endsWith('_DETAIL')) continue;
        const prev = byCode.get(code);
        const add = m.area_microha === null ? null : m.area_microha / 1_000_000;
        byCode.set(code, prev === undefined ? add : prev === null || add === null ? (prev ?? add) : prev + add);
      }
    }
  }
  return [...byCode].map(([code, ha]) => ({ code, ha })).sort((a, b) => (b.ha ?? -1) - (a.ha ?? -1));
}

/* ------------------------------------------------------------ площадь */

export interface FarmArea {
  /** Одна цифра карточки — земля хозяйства по реестру (вся площадь), га. */
  ha: number | null;
  /** Пашня по землепользованиям, га; `null` — не названа. */
  arableHa: number | null;
  /** Цифры расходятся больше чем на 0,01 га — нужна пометка с определением. */
  differs: boolean;
  /** Определение площади словами. */
  note: string;
}

const microha = (v: number | null | undefined) => (v === null || v === undefined ? null : v / 1_000_000);

/**
 * Земля хозяйства: одна цифра (вся земля по реестру) и определение. Пашня по
 * землепользованиям — другая величина (без дорог, лесополос, построек); она
 * называется в пояснении, а не второй цифрой рядом.
 */
export function farmArea(totalMicroha: number | null | undefined, arableMicroha: number | null | undefined): FarmArea {
  const ha = microha(totalMicroha);
  const arableHa = microha(arableMicroha);
  const differs = ha !== null && arableHa !== null && Math.abs(ha - arableHa) > 0.01;
  const note =
    ha === null
      ? tr(m.areaNone)
      : differs && arableHa !== null
        ? tr(m.areaDiffers, { total: haText(ha), arable: haText(arableHa) })
        : tr(m.areaSame);
  return { ha, arableHa, differs, note };
}

/* ------------------------------------------------------------ сигналы */

export interface SituationLike {
  farmId: string;
  key?: string;
  overdue: number;
  dueSoon: number;
  conflicts: number;
  waterGapObligations?: number | null;
  overlapObligations: number;
  /** Земля (каждый гектар один раз) с работой после срока без отчёта (`overdue_area` очереди, №690); `null` — не названа. */
  overdueArea?: number | null;
}

/** Числа значков из строки очереди сервера (тот же смысл, что `signalCounts` потока A). */
export function countsFromSituation(s: SituationLike | null | undefined, quarantine: number | null): SignalCounts {
  return {
    deadlines: s ? (s.overdue > 0 ? s.overdue : s.dueSoon > 0 ? s.dueSoon : 0) : 0,
    waterM3: null,
    waterObligations: s ? (s.waterGapObligations ?? (s.overlapObligations > 0 ? s.overlapObligations : 0)) : 0,
    conflicts: s ? s.conflicts : 0,
    quarantine,
    deadlinesHa: s && s.overdue > 0 && typeof s.overdueArea === 'number' ? s.overdueArea : null,
  };
}

export function hasSignal(c: Partial<SignalCounts>): boolean {
  return Boolean(c.deadlines || c.waterM3 || c.waterObligations || c.conflicts || c.quarantine);
}

/**
 * Сколько субъектов очереди ситуаций не входит в реестр хозяйств (agroai-b48.3).
 *
 * «Хозяйств с сигналами» считает только хозяйства реестра, а очередь «Где нужно
 * разобраться» — всех субъектов, у которых есть сигнал, в том числе резерв и
 * сводные строки дехканских хозяйств, которых в реестре хозяйств нет. Это
 * законная разница основ, и подсказка называет её ЧИСЛОМ из тех же ответов, а
 * не словом «может быть на одно больше».
 */
export function queueOutsideRegistry(registryIds: ReadonlySet<string>, queueIds: Iterable<string>): number {
  let outside = 0;
  for (const id of new Set(queueIds)) if (!registryIds.has(id)) outside += 1;
  return outside;
}

/**
 * Тяжесть для сортировки «с проблемами сверху»: карантин и расхождения —
 * вперёд, затем объём — ГЕКТАРЫ работ после срока (решение №645: про землю —
 * гектары), а при неназванной площади — число работ.
 */
export function signalWeight(c: Partial<SignalCounts>): number {
  const deadlines = typeof c.deadlinesHa === 'number' ? c.deadlinesHa : (c.deadlines ?? 0);
  return (c.quarantine ? 1_000_000 : 0) + (c.conflicts ? 100_000 : 0) + Math.min(99_999, deadlines) + (c.waterObligations ?? 0) / 1000;
}

export type QuarantineValue = 'green' | 'watch' | 'red';

/** Открытые поручения (`open`, `in_progress`) по хозяйствам. */
export function openAssignmentsByFarm(
  items: ReadonlyArray<{ status: string; key_parts: { actor_id: number | null } | null }>,
): Map<string, number> {
  const out = new Map<string, number>();
  for (const a of items) {
    const id = a.key_parts?.actor_id;
    if (id === null || id === undefined) continue;
    if (a.status !== 'open' && a.status !== 'in_progress') continue;
    out.set(String(id), (out.get(String(id)) ?? 0) + 1);
  }
  return out;
}

/* ------------------------------------------------------------ строки реестра */

export interface RegistryRow {
  id: string;
  name: string;
  massifIds: string[];
  massifNames: string[];
  /** Земля хозяйства по массивам, основной — первый (07.10.2026). */
  massifLand?: { id: string; name: string; ha: number | null }[];
  areaHa: number | null;
  contours: number;
  crops: string[];
  signals: SignalCounts;
  flagged: boolean;
  openAssignments: number | null;
}

export type RegistrySort = 'problems' | 'name' | 'land';

const byName = (a: RegistryRow, b: RegistryRow) => a.name.localeCompare(b.name, 'ru');

export function sortRegistry(rows: ReadonlyArray<RegistryRow>, sort: RegistrySort): RegistryRow[] {
  const copy = [...rows];
  if (sort === 'name') return copy.sort(byName);
  if (sort === 'land') return copy.sort((a, b) => (b.areaHa ?? -1) - (a.areaHa ?? -1) || byName(a, b));
  return copy.sort((a, b) => signalWeight(b.signals) - signalWeight(a.signals) || (b.areaHa ?? -1) - (a.areaHa ?? -1) || byName(a, b));
}

export interface RegistryFilter {
  query: string;
  massifId: string | null;
  onlyFlagged: boolean;
}

export function filterRegistry(rows: ReadonlyArray<RegistryRow>, f: RegistryFilter): RegistryRow[] {
  const q = f.query.trim().toLowerCase();
  return rows.filter(
    (r) =>
      (!q || r.name.toLowerCase().includes(q)) &&
      (!f.massifId || r.massifIds.includes(f.massifId)) &&
      (!f.onlyFlagged || r.flagged),
  );
}

/* ------------------------------------------------------------ слой карты */

/**
 * Слой «Сигналы хозяйств»: контур красится по худшему сигналу своего
 * хозяйства. Карантин — по самому контуру (критичное состояние объекта),
 * остальное — по хозяйству; «без отчёта» не красный (ADR-0009).
 */
export function signalsLayer(
  rows: ReadonlyArray<RegistryRow>,
  contourFarm: ReadonlyMap<string, string>,
  quarantineContours: ReadonlyMap<string, QuarantineValue>,
): ValuesLayer {
  const byFarm = new Map(rows.map((r) => [r.id, r.signals]));
  const byId: Record<string, string> = {};
  for (const [contour, farm] of contourFarm) {
    const q = quarantineContours.get(contour);
    if (q === 'red') { byId[contour] = 'quarantine'; continue; }
    if (q === 'watch') { byId[contour] = 'watch'; continue; }
    const s = byFarm.get(farm);
    if (!s) continue;
    if (s.conflicts) byId[contour] = 'conflicts';
    else if (s.deadlines) byId[contour] = 'deadlines';
    else if (s.waterObligations || s.waterM3) byId[contour] = 'water';
  }
  return {
    title: tr(m.layerTitle),
    byId,
    legend: [
      { key: 'quarantine', label: tr(m.layerQuarantine), tone: 'critical' },
      { key: 'watch', label: tr(m.layerWatch), tone: 'progress' },
      { key: 'deadlines', label: tr(m.layerDeadlines), tone: 'warning' },
      { key: 'conflicts', label: tr(m.layerConflicts), tone: 'neutral' },
      { key: 'water', label: tr(m.layerWater), tone: 'water' },
    ],
    noneLabel: tr(m.layerNone),
  };
}

/* ------------------------------------------------------------ стадии хозяйства */

export interface StageShare {
  id: string;
  name: string;
  order: number;
  total: number;
  reported: number;
  /** Окно стадии ещё не открылось — «ещё не срок», не ноль. */
  ahead: boolean;
}

/** Доля отчётов по стадиям цикла хозяйства из работ картины (`summary.works`). */
export function stageShares(
  works: ReadonlyArray<{
    stage: { id: string; name: string; order: number } | null;
    window_from: string | null;
    totals: { obligations: { total: number; reported: number } };
  }>,
  asOf: string | null,
): StageShare[] {
  const by = new Map<string, StageShare>();
  for (const w of works) {
    if (!w.stage) continue;
    const hit = by.get(w.stage.id) ?? { id: w.stage.id, name: w.stage.name, order: w.stage.order, total: 0, reported: 0, ahead: true };
    hit.total += w.totals.obligations.total;
    hit.reported += w.totals.obligations.reported;
    if (!asOf || !w.window_from || w.window_from <= asOf) hit.ahead = false;
    by.set(w.stage.id, hit);
  }
  return [...by.values()].filter((s) => s.total > 0).sort((a, b) => a.order - b.order);
}

/** Недели воды не позже даты состояния: сервер отдаёт заявки и после неё. */
export function weeksUpTo<T extends { key: string }>(rows: ReadonlyArray<T>, asOf: string | null): T[] {
  return asOf ? rows.filter((r) => r.key.slice(0, 10) <= asOf) : [...rows];
}
